// @ts-strict

import {
  Component,
  ComponentRef,
  EventEmitter,
  Input,
  OnChanges, OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { LMDrillAroundNode } from '@components/drillaround/lm-drill-around-wc';
import { LMDrillViewPdfComponent, LMDrillViewUnknownComponent, LMFormComponent, LMListComponent } from "@app/components";
import { LMDrillPointListComponent } from "@components/drillaround/lm-drill-point-list.component";
import { Subscription } from "rxjs";

@Component({
  selector: 'lm-drill-point-view',
  standalone: false,
  // templateUrl: './lm-drill-point-view.component.html',
  template: `<ng-template #DrillViewContainer></ng-template>`,
  styleUrls: [ './lm-drill-point-view.component.scss' ],
})
export class LMDrillPointViewComponent implements OnChanges, OnDestroy {

  @ViewChild('DrillViewContainer', { read: ViewContainerRef }) drillViewContainerRef: ViewContainerRef | undefined;

  private componentRef:
    ComponentRef<LMFormComponent> |
    ComponentRef<LMListComponent> |
    ComponentRef<LMDrillViewUnknownComponent> |
    ComponentRef<LMDrillViewPdfComponent> |
    ComponentRef<LMDrillPointListComponent> |
    undefined;

  @Input() info!: LMDrillAroundViewInfo;
  @Input() selectedNode!: LMDrillAroundNode;

  @Output() drillActionSelectedEvent = new EventEmitter<LMDrillAroundNode>();
  @Output() drillAroundEvent = new EventEmitter<LMDrillAroundRequest>();
  @Output() sendContextMessageEvent = new EventEmitter<any>();

  private drillAroundEventEventSub: Subscription | undefined;
  private drillPointSelectedEventSub: Subscription | undefined;
  private sendContextMessageEventSub: Subscription | undefined;

  constructor() {
    // no-op
  }
  
  ngOnChanges(changes: SimpleChanges) {
    if (changes['info']) {
      setTimeout(() =>
        this.destroyAndReload(), 0);
    }
  }

  private destroyAndReload() {
    if (!this.info) {
      console.log('info object is undefined');
      return;
    }

    if (!this.drillViewContainerRef) {
      console.log('No drillViewContainerRef');
      return;
    }

    this.destroyCurrentView();

    switch (this.info.type) {
      case 'list':
        this.loadListComponent();
        break;

      case 'form':
        this.loadFormComponent();
        break;

      case 'unknown':
        this.loadUnknownComponent();
        break;

      case 'pdf':
        this.loadPdfComponent();
        break;

      case 'record':
      case 'panel':
        this.loadDrillPointComponent();
        break;

      default:
        break;
    }
  }

  private destroyCurrentView(): void {
    this.drillAroundEventEventSub?.unsubscribe();
    this.drillPointSelectedEventSub?.unsubscribe();
    this.sendContextMessageEventSub?.unsubscribe();

    if (this.drillViewContainerRef && this.componentRef) {
      this.drillViewContainerRef.detach();
      this.componentRef.destroy();
    }
  }

  private loadListComponent(): void {
    // Using separate variable to avoid TypeScript union type issues.
    // this.componentRef.instance doesn't know which specific component type it is.
    // We just don't see the typescript error due to ts-strict-ignore being used in most components.
    const listComponentRef = this.drillViewContainerRef!.createComponent(LMListComponent);
    this.componentRef = listComponentRef;
    this.drillViewContainerRef!.insert(listComponentRef.hostView);
    listComponentRef.instance.listUrlInfo = this.info.urlInfo;
    listComponentRef.instance.listSpec = this.info.spec as LMListSpec;
    listComponentRef.instance.isDrillPointRootComponent = true;

    this.drillAroundEventEventSub = listComponentRef.instance.drillAroundEvent.subscribe((event: LMDrillAroundRequest) => {
      this.drillAroundEvent.emit(event);
    });
    this.sendContextMessageEventSub = listComponentRef.instance.sendContextMessageEvent.subscribe((event: any) => {
      this.sendContextMessageEvent.emit(event);
    });
  }

  private loadFormComponent(): void {
    const formComponentRef = this.drillViewContainerRef!.createComponent(LMFormComponent);
    this.componentRef = formComponentRef;
    this.drillViewContainerRef!.insert(formComponentRef.hostView);
    formComponentRef.instance.formUrlInfo = this.info.urlInfo;
    formComponentRef.instance.formSpec = this.info.spec;
    formComponentRef.instance.isDrillPointRootComponent = true;

    this.drillAroundEventEventSub = formComponentRef.instance.drillAroundEvent.subscribe((event: LMDrillAroundRequest) => {
      this.drillAroundEvent.emit(event);
    });
    this.sendContextMessageEventSub = formComponentRef.instance.sendContextMessageEvent.subscribe((event: any) => {
      this.sendContextMessageEvent.emit(event);
    });
  }

  private loadUnknownComponent(): void {
    const unknownComponentRef = this.drillViewContainerRef!.createComponent(LMDrillViewUnknownComponent);
    this.componentRef = unknownComponentRef;
    this.drillViewContainerRef!.insert(unknownComponentRef.hostView);
    unknownComponentRef.instance.info = this.info;
  }

  private loadPdfComponent(): void {
    const viewPdfComponentRef = this.drillViewContainerRef!.createComponent(LMDrillViewPdfComponent);
    this.componentRef = viewPdfComponentRef;
    this.drillViewContainerRef!.insert(viewPdfComponentRef.hostView);
    viewPdfComponentRef.instance.info = this.info;
  }

  private loadDrillPointComponent(): void {
    const drillPointComponentRef = this.drillViewContainerRef!.createComponent(LMDrillPointListComponent);
    this.componentRef = drillPointComponentRef;
    this.drillViewContainerRef!.insert(drillPointComponentRef.hostView);

    drillPointComponentRef.instance.info = this.info;
    drillPointComponentRef.instance.selectedNode = this.selectedNode;

    this.drillPointSelectedEventSub = drillPointComponentRef.instance.drillPointSelected.subscribe((event: LMDrillAroundRequest) => {
      this.drillActionSelectedEvent.emit(event);
    });
  }

  ngOnDestroy() {
    this.destroyCurrentView();
  }
}
